import React, { useMemo, useState } from 'react'
import GuideLayout from '@/components/help/GuideLayout'
import GuideSection from '@/components/help/GuideSection'
import GuideWidget from '@/components/help/GuideWidget'
import GuideTip from '@/components/help/GuideTip'
import GuideRelatedLinks from '@/components/help/GuideRelatedLinks'
import { AlertTriangle, Ban, Shield, Scale, Zap } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { SharedProps } from '@/types/shared'

// ── Illustrative constants — mirror app/lib/app/rate_engine/stages/market_position_stage.rb ──
// These power the local "Try it" widget only. The engine is the source of truth; nothing
// here calls the backend or reads property data (self-contained, like every other guide widget).
type Sensitivity = 'none' | 'conservative' | 'balanced' | 'aggressive'

const SENSITIVITY_PRESETS: Record<
  Exclude<Sensitivity, 'none'>,
  { label: string; scale: number; cap: number }
> = {
  conservative: { label: 'Conservative', scale: 0.6, cap: 8 },
  balanced: { label: 'Balanced', scale: 1.0, cap: 12 },
  aggressive: { label: 'Aggressive', scale: 1.4, cap: 18 },
}

const SENSITIVITY_TOGGLE: Array<{ value: Sensitivity; label: string; icon: React.ElementType }> = [
  { value: 'none', label: 'Disabled', icon: Ban },
  { value: 'conservative', label: 'Conservative', icon: Shield },
  { value: 'balanced', label: 'Balanced', icon: Scale },
  { value: 'aggressive', label: 'Aggressive', icon: Zap },
]

// rate_index = your rate ÷ compset median. < 1.0 = cheaper than market, > 1.0 = pricier.
const BANDS: Array<{ max: number; basePct: number; label: string }> = [
  { max: 0.85, basePct: 10, label: 'Significantly underpriced' },
  { max: 0.95, basePct: 4, label: 'Slightly underpriced' },
  { max: 1.05, basePct: 0, label: 'At market' },
  { max: 1.15, basePct: -4, label: 'Slightly overpriced' },
  { max: Infinity, basePct: -10, label: 'Significantly overpriced' },
]

const SOLD_OUT_BOOST = 3 // %, scaled by sensitivity, only when the move is already upward

function bandFor(rateIndex: number) {
  return BANDS.find((b) => rateIndex < b.max) ?? BANDS[BANDS.length - 1]
}

/**
 * Self-contained playground. Pick a rate index and a sensitivity (plus an optional sold-out
 * toggle and a max-cap) and watch the band, base move, and final capped adjustment resolve —
 * the same shape of arithmetic the engine runs, with illustrative numbers.
 */
function MarketPositionPlayground() {
  const [rateIndex, setRateIndex] = useState(0.8)
  const [sensitivity, setSensitivity] = useState<Sensitivity>('balanced')
  const [soldOut, setSoldOut] = useState(false)
  const [maxCap, setMaxCap] = useState(12)

  const result = useMemo(() => {
    const band = bandFor(rateIndex)

    if (sensitivity === 'none') {
      return {
        band,
        basePct: band.basePct,
        scaledPct: 0,
        soldOutBoost: 0,
        cappedPct: 0,
        finalPct: 0,
        disabled: true,
        capHit: false,
      }
    }

    const preset = SENSITIVITY_PRESETS[sensitivity]
    let scaledPct = band.basePct * preset.scale

    // Sold-out boost: only when the move is already upward (modifier ≥ 0).
    let soldOutBoost = 0
    if (soldOut && scaledPct >= 0) {
      soldOutBoost = SOLD_OUT_BOOST * preset.scale
      scaledPct += soldOutBoost
    }

    // Two caps apply: the sensitivity preset cap and the user-set max — the tighter wins.
    const effectiveCap = Math.min(preset.cap, maxCap)
    const cappedRaw = Math.max(-effectiveCap, Math.min(effectiveCap, scaledPct))
    // House rule: round the percentage to 1 decimal before it is applied.
    const finalPct = Math.round(cappedRaw * 10) / 10

    return {
      band,
      basePct: band.basePct,
      scaledPct: Math.round(scaledPct * 10) / 10,
      soldOutBoost: Math.round(soldOutBoost * 10) / 10,
      cappedPct: Math.round(cappedRaw * 10) / 10,
      finalPct,
      disabled: false,
      capHit: Math.abs(scaledPct) > effectiveCap + 1e-9,
    }
  }, [rateIndex, sensitivity, soldOut, maxCap])

  // Illustrative base rate so the user can see the move land in money.
  const baseRate = 100
  const adjusted = useMemo(
    () => Math.round(baseRate * (1 + result.finalPct / 100)),
    [result.finalPct]
  )

  const direction =
    result.finalPct > 0 ? 'up' : result.finalPct < 0 ? 'down' : 'flat'
  const sign = result.finalPct > 0 ? '+' : ''

  return (
    <div className="space-y-5">
      {/* Rate index slider */}
      <div className="space-y-1.5">
        <div className="flex items-baseline justify-between">
          <label className="text-xs font-medium text-foreground">
            Rate index &mdash; your rate &divide; compset median
          </label>
          <span className="text-xs font-semibold tabular-nums text-foreground">
            {rateIndex.toFixed(2)}&times;
          </span>
        </div>
        <input
          type="range"
          min={0.7}
          max={1.3}
          step={0.01}
          value={rateIndex}
          onChange={(e) => setRateIndex(parseFloat(e.target.value))}
          className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-fuchsia-500"
        />
        <div className="flex justify-between text-[10px] text-muted-foreground">
          <span>0.70&times; (much cheaper)</span>
          <span>1.00&times; (at market)</span>
          <span>1.30&times; (much pricier)</span>
        </div>
      </div>

      {/* Sensitivity toggle */}
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-foreground">Sensitivity</label>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {SENSITIVITY_TOGGLE.map((opt) => {
            const Icon = opt.icon
            const isActive = sensitivity === opt.value
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setSensitivity(opt.value)}
                className={cn(
                  'flex items-center justify-center gap-1.5 rounded-lg border px-2 py-2 text-xs font-medium transition-all cursor-pointer',
                  isActive
                    ? 'border-fuchsia-400 bg-fuchsia-50 text-fuchsia-700 ring-1 ring-fuchsia-300/50'
                    : 'border-border/60 text-muted-foreground hover:border-border hover:bg-muted/30'
                )}
              >
                <Icon className="h-3.5 w-3.5" />
                {opt.label}
              </button>
            )
          })}
        </div>
      </div>

      {/* Secondary controls */}
      <div className="grid grid-cols-2 gap-4">
        <button
          type="button"
          onClick={() => setSoldOut((v) => !v)}
          disabled={result.disabled}
          className={cn(
            'flex items-center justify-between rounded-lg border px-3 py-2 text-xs font-medium transition-all',
            result.disabled
              ? 'cursor-not-allowed border-border/40 text-muted-foreground/50'
              : soldOut
                ? 'cursor-pointer border-fuchsia-400 bg-fuchsia-50 text-fuchsia-700'
                : 'cursor-pointer border-border/60 text-muted-foreground hover:border-border hover:bg-muted/30'
          )}
        >
          <span>Competitors selling out</span>
          <span
            className={cn(
              'ml-2 inline-flex h-4 w-7 shrink-0 items-center rounded-full px-0.5 transition-colors',
              soldOut && !result.disabled ? 'bg-fuchsia-500' : 'bg-slate-300'
            )}
          >
            <span
              className={cn(
                'h-3 w-3 rounded-full bg-white transition-transform',
                soldOut && !result.disabled ? 'translate-x-3' : 'translate-x-0'
              )}
            />
          </span>
        </button>
        <div className="space-y-1">
          <label className="text-[11px] font-medium text-muted-foreground">
            Max adjustment cap
          </label>
          <div className="flex items-center gap-2">
            <input
              type="range"
              min={2}
              max={20}
              step={1}
              value={maxCap}
              onChange={(e) => setMaxCap(parseInt(e.target.value))}
              className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-fuchsia-500"
            />
            <span className="w-10 shrink-0 text-right text-xs font-semibold tabular-nums text-foreground">
              &plusmn;{maxCap}%
            </span>
          </div>
        </div>
      </div>

      {/* Result */}
      <div className="rounded-lg border border-border/60 bg-white p-4">
        {result.disabled ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Ban className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span>
              Sensitivity is <strong className="text-foreground">Disabled</strong> &mdash; Market
              Position is off and the rate passes through unchanged.
            </span>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs">
              <div>
                <span className="text-muted-foreground">Band: </span>
                <span className="font-semibold text-foreground">{result.band.label}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Base move: </span>
                <span className="font-semibold tabular-nums text-foreground">
                  {result.basePct > 0 ? '+' : ''}
                  {result.basePct}%
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">After sensitivity &times;: </span>
                <span className="font-semibold tabular-nums text-foreground">
                  {result.scaledPct > 0 ? '+' : ''}
                  {result.scaledPct}%
                </span>
              </div>
              {result.soldOutBoost > 0 && (
                <div>
                  <span className="text-muted-foreground">Sold-out boost: </span>
                  <span className="font-semibold tabular-nums text-fuchsia-600">
                    +{result.soldOutBoost}%
                  </span>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between rounded-lg bg-muted/40 px-4 py-3">
              <div>
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Final adjustment
                </p>
                <p
                  className={cn(
                    'text-lg font-bold tabular-nums',
                    direction === 'up' && 'text-emerald-600',
                    direction === 'down' && 'text-rose-600',
                    direction === 'flat' && 'text-foreground'
                  )}
                >
                  {sign}
                  {result.finalPct}%
                </p>
                {result.capHit && (
                  <p className="text-[10px] font-medium text-amber-600">capped at &plusmn;{Math.min(SENSITIVITY_PRESETS[sensitivity as Exclude<Sensitivity, 'none'>].cap, maxCap)}%</p>
                )}
              </div>
              <div className="text-right">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  On a &pound;{baseRate} rate
                </p>
                <p className="text-lg font-bold tabular-nums text-foreground">
                  &pound;{baseRate} &rarr; &pound;{adjusted}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default function MarketPosition({ currentUser, properties }: Pick<SharedProps, 'currentUser' | 'properties'>) {
  return (
    <GuideLayout
      currentUser={currentUser}
      properties={properties}
      title="Market Position"
      subtitle="Price relative to your competitive set — boost when you're cheaper than the market, soften when you're pricier."
    >
      {/* ── WIP / preview banner — mirrors the amber callout on the settings page ── */}
      <div className="rounded-xl border border-amber-300 bg-amber-50/50 p-5 shadow-sm">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
          <div className="space-y-1">
            <h3 className="text-sm font-semibold text-foreground">Preview — not fully live yet</h3>
            <p className="text-sm leading-relaxed text-muted-foreground">
              The Market Position pricing logic is <strong className="text-foreground">built and
              unit-tested</strong>, but the <strong className="text-foreground">competitor-rate
              feed</strong> that powers it &mdash; the <strong className="text-foreground">Market
              Intelligence</strong> layer that collects rates for your competitive set &mdash; is
              still being connected. Until that feed is live and collecting for your compset, this
              stage stays <strong className="text-foreground">inert</strong>: it will{' '}
              <em>no-op even if you pick a sensitivity</em>, so it won&rsquo;t move your rates yet.
            </p>
            <p className="text-sm leading-relaxed text-muted-foreground">
              You can read this guide and set your preference now. Market Position will begin
              adjusting rates automatically once market data is collecting for your compset &mdash;
              no further action needed on your part.
            </p>
          </div>
        </div>
      </div>

      <GuideSection title="What Market Position Does">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Your other pricing rules &mdash; base rates, seasons, day-of-week, demand &mdash; price
          your property from the <strong className="text-foreground">inside out</strong>: from your
          costs, your patterns, and your own booking pace. None of them look at what the hotel down
          the road is charging tonight.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Market Position is the one stage that prices from the{' '}
          <strong className="text-foreground">outside in</strong>. It compares your computed rate to
          your <strong className="text-foreground">competitive set</strong> &mdash; your{' '}
          <strong className="text-foreground">&ldquo;compset&rdquo;</strong>, the handful of nearby
          properties a guest would realistically choose between &mdash; and nudges your price toward
          the market when you&rsquo;ve drifted too far from it.
        </p>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            <strong className="text-foreground">Cheaper than the market?</strong> You&rsquo;re
            likely leaving money on the table &mdash; rates nudge <em>up</em> to capture it.
          </li>
          <li>
            <strong className="text-foreground">More expensive than the market?</strong> You risk
            losing the booking to a cheaper neighbour &mdash; rates soften <em>down</em> to stay
            competitive.
          </li>
        </ul>
        <p className="text-sm leading-relaxed text-muted-foreground">
          It is <strong className="text-foreground">stage 8</strong> of the 13-stage rate pipeline,
          running after the demand stages and before the guardrails. Like every other stage, it only
          ever <em>adjusts</em> the rate the pipeline has built so far &mdash; it never sets the
          price on its own.
        </p>
      </GuideSection>

      <GuideSection title="The Rate Index">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Everything Market Position does hinges on one number, the{' '}
          <strong className="text-foreground">rate index</strong>:
        </p>
        <div className="rounded-lg border bg-muted/50 p-4">
          <p className="text-sm text-muted-foreground">
            <strong className="text-foreground">Rate index</strong> = your rate &divide; the{' '}
            <strong className="text-foreground">compset median</strong> (the middle price across your
            competitors)
          </p>
        </div>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            <strong className="text-foreground">Index &lt; 1.0</strong> &mdash; you&rsquo;re{' '}
            <em>cheaper</em> than the market. An index of 0.80 means you&rsquo;re priced at 80% of
            the compset median.
          </li>
          <li>
            <strong className="text-foreground">Index = 1.0</strong> &mdash; you&rsquo;re priced{' '}
            <em>exactly</em> at the market median.
          </li>
          <li>
            <strong className="text-foreground">Index &gt; 1.0</strong> &mdash; you&rsquo;re{' '}
            <em>pricier</em> than the market. An index of 1.20 means 20% above the compset median.
          </li>
        </ul>
        <p className="text-sm leading-relaxed text-muted-foreground">
          We use the <strong className="text-foreground">median</strong>, not the average, on
          purpose: one wildly-priced competitor (a luxury suite, or a fire-sale) can&rsquo;t drag the
          benchmark around. The median is the typical price a guest actually sees.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Your own Booking.com listing is never counted as a competitor, and in a combined inventory neither is any other
          listing of the same building, so your own prices can never become the market this rule follows.
        </p>
      </GuideSection>

      <GuideSection title="Adjustment Bands">
        <p className="text-sm leading-relaxed text-muted-foreground">
          The rate index falls into one of five bands. Each band has a{' '}
          <strong className="text-foreground">base move</strong> &mdash; the adjustment{' '}
          <em>before</em> your sensitivity setting scales it (see the next section). There&rsquo;s a
          deliberate <strong className="text-foreground">dead zone</strong> around the market
          (0.95&ndash;1.05&times;) where the engine does nothing, so prices that are already roughly
          right are left alone.
        </p>

        <div className="my-4 overflow-hidden rounded-xl border border-border/60 bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/40 bg-slate-50/50">
                <th className="px-4 py-2 text-left text-xs font-semibold text-foreground">Rate index</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-foreground">Position</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-foreground">Direction</th>
                <th className="px-4 py-2 text-right text-xs font-semibold text-foreground">Base move</th>
              </tr>
            </thead>
            <tbody className="text-xs">
              <tr className="border-b border-border/20">
                <td className="px-4 py-2 font-medium tabular-nums text-foreground">&lt; 0.85&times;</td>
                <td className="px-4 py-2 text-muted-foreground">Significantly underpriced</td>
                <td className="px-4 py-2 text-emerald-600">Boost toward market</td>
                <td className="px-4 py-2 text-right font-semibold tabular-nums text-emerald-600">+10%</td>
              </tr>
              <tr className="border-b border-border/20">
                <td className="px-4 py-2 font-medium tabular-nums text-foreground">0.85 &ndash; 0.95&times;</td>
                <td className="px-4 py-2 text-muted-foreground">Slightly underpriced</td>
                <td className="px-4 py-2 text-emerald-600">Small boost</td>
                <td className="px-4 py-2 text-right font-semibold tabular-nums text-emerald-600">+4%</td>
              </tr>
              <tr className="border-b border-border/20 bg-slate-50/40">
                <td className="px-4 py-2 font-medium tabular-nums text-foreground">0.95 &ndash; 1.05&times;</td>
                <td className="px-4 py-2 text-muted-foreground">At market (dead zone)</td>
                <td className="px-4 py-2 text-muted-foreground">No change</td>
                <td className="px-4 py-2 text-right font-semibold tabular-nums text-muted-foreground">0%</td>
              </tr>
              <tr className="border-b border-border/20">
                <td className="px-4 py-2 font-medium tabular-nums text-foreground">1.05 &ndash; 1.15&times;</td>
                <td className="px-4 py-2 text-muted-foreground">Slightly overpriced</td>
                <td className="px-4 py-2 text-rose-600">Small reduction</td>
                <td className="px-4 py-2 text-right font-semibold tabular-nums text-rose-600">&minus;4%</td>
              </tr>
              <tr>
                <td className="px-4 py-2 font-medium tabular-nums text-foreground">&ge; 1.15&times;</td>
                <td className="px-4 py-2 text-muted-foreground">Significantly overpriced</td>
                <td className="px-4 py-2 text-rose-600">Soften to compete</td>
                <td className="px-4 py-2 text-right font-semibold tabular-nums text-rose-600">&minus;10%</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-sm leading-relaxed text-muted-foreground">
          These base moves are intentionally modest. Market Position is a{' '}
          <strong className="text-foreground">nudge toward the market</strong>, not a snap to it
          &mdash; it closes part of the gap each cycle rather than yanking your rate onto the median
          in one move.
        </p>
      </GuideSection>

      <GuideSection title="Sensitivity: Scale and Cap">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Sensitivity is your single dial for how strongly Market Position reacts. It does two things
          to the base move: <strong className="text-foreground">scales</strong> it by a factor, and{' '}
          <strong className="text-foreground">caps</strong> the result so it can never run away.
        </p>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            <strong className="text-foreground">Disabled</strong> &mdash; Market Position is off.
            Rates are set purely by your other rules. (This is also the effective state today until
            the competitor-rate feed is live.)
          </li>
          <li>
            <strong className="text-foreground">Conservative</strong> &mdash; scales the base move by{' '}
            <strong className="text-foreground">&times;0.6</strong>, capped at{' '}
            <strong className="text-foreground">&plusmn;8%</strong>. Gentle nudges for
            price-sensitive markets where big swings cost bookings.
          </li>
          <li>
            <strong className="text-foreground">Balanced</strong> &mdash; scales by{' '}
            <strong className="text-foreground">&times;1.0</strong> (the base move as-is), capped at{' '}
            <strong className="text-foreground">&plusmn;12%</strong>.{' '}
            <strong className="text-foreground">The recommended default</strong> for most properties.
          </li>
          <li>
            <strong className="text-foreground">Aggressive</strong> &mdash; scales by{' '}
            <strong className="text-foreground">&times;1.4</strong>, capped at{' '}
            <strong className="text-foreground">&plusmn;18%</strong>. Tracks the market closely for
            highly competitive areas where being mispriced costs bookings fast.
          </li>
        </ul>
        <p className="text-sm leading-relaxed text-muted-foreground">
          On top of the preset cap you can set your own{' '}
          <strong className="text-foreground">maximum adjustment %</strong> on the settings page &mdash;
          a hard limit on how far Market Position alone can move a rate. When both apply, the{' '}
          <strong className="text-foreground">tighter</strong> of the two wins. (Your room
          floor/ceiling and the Price Guardrails still sit underneath everything as the final
          backstop.)
        </p>
        <div className="rounded-lg border bg-muted/50 p-4">
          <p className="text-sm text-muted-foreground">
            <strong className="text-foreground">Example:</strong> rate index 0.80 (significantly
            underpriced) gives a +10% base move. On <strong className="text-foreground">Balanced</strong>{' '}
            that&rsquo;s +10% &times; 1.0 = <strong className="text-foreground">+10%</strong>. On{' '}
            <strong className="text-foreground">Conservative</strong> it&rsquo;s +10% &times; 0.6 ={' '}
            <strong className="text-foreground">+6%</strong>. On{' '}
            <strong className="text-foreground">Aggressive</strong> it would be +14%, but the
            &plusmn;18% cap lets it through in full at <strong className="text-foreground">+14%</strong>.
          </p>
        </div>
      </GuideSection>

      <GuideWidget
        title="Market Position Calculator"
        footnote="Illustrative numbers on a £100 base rate, computed locally in your browser. It uses the same bands, scaling, and caps as the engine, but the live stage runs against your real compset median — and only once the competitor-rate feed is collecting."
      >
        <MarketPositionPlayground />
      </GuideWidget>

      <GuideSection title="The Sold-Out Boost">
        <p className="text-sm leading-relaxed text-muted-foreground">
          The compset median tells you about <em>price</em>. But the market also gives a{' '}
          <strong className="text-foreground">supply</strong> signal: when competitors start{' '}
          <strong className="text-foreground">selling out</strong>, there are fewer alternatives
          left, and the rooms that remain &mdash; including yours &mdash; can command more.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          So when competitors are selling out <em>and</em> the move is already upward, Market
          Position adds an extra <strong className="text-foreground">+3% boost</strong> (scaled by
          your sensitivity, just like the base move) on top &mdash; still bounded by your cap. It is
          deliberately one-directional: scarcity only ever <em>adds</em> to an upward move, it never
          softens a reduction. Toggle &ldquo;Competitors selling out&rdquo; in the calculator above
          to see it land.
        </p>
      </GuideSection>

      <GuideSection title="How It Fits the Pipeline">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Market Position is one stage in a deterministic chain, and it plays by the same house
          rules as the rest:
        </p>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            The percentage is <strong className="text-foreground">rounded to one decimal</strong>,
            applied to the running rate, and then the rate is{' '}
            <strong className="text-foreground">rounded to your rounding increment</strong> (default
            &pound;1). No fractional pennies leak between stages.
          </li>
          <li>
            It runs <strong className="text-foreground">after</strong> the demand stages (so it
            judges your demand-adjusted price against the market) and{' '}
            <strong className="text-foreground">before</strong> the clamp and Price Guardrails (so
            those always have the final say on the absolute floor and ceiling).
          </li>
          <li>
            Every adjustment is <strong className="text-foreground">fully traceable</strong>. The
            Prices tab of History records the rate index, the band, the sold-out boost, the
            sensitivity, and the final modifier %, exactly like every other stage. Nothing about the
            price is a black box.
          </li>
        </ul>
      </GuideSection>

      <GuideSection title="What Works Today vs. What's Coming">
        <p className="text-sm leading-relaxed text-muted-foreground">
          To set expectations honestly while this rolls out:
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4">
            <h4 className="text-sm font-semibold text-emerald-800">Live today</h4>
            <ul className="mt-2 ml-4 list-disc space-y-1.5 text-xs leading-relaxed text-muted-foreground">
              <li>The pricing logic &mdash; bands, sensitivity scaling, caps, the sold-out boost &mdash; is built and unit-tested.</li>
              <li>You can choose your sensitivity and max-adjustment cap on the settings page now; your choice is saved.</li>
              <li>The stage is wired into the pipeline at position 8 and ready to act the moment it has data.</li>
            </ul>
          </div>
          <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-4">
            <h4 className="text-sm font-semibold text-amber-800">Still coming</h4>
            <ul className="mt-2 ml-4 list-disc space-y-1.5 text-xs leading-relaxed text-muted-foreground">
              <li>The <strong className="text-foreground">Market Intelligence</strong> feed that collects competitor rates for your compset is still being connected.</li>
              <li>Until it&rsquo;s collecting, there&rsquo;s no compset median, so no rate index can be computed &mdash; and the stage <strong className="text-foreground">no-ops</strong>.</li>
              <li>Once the feed is live for your compset, Market Position starts adjusting rates automatically &mdash; no extra setup.</li>
            </ul>
          </div>
        </div>
        <p className="text-sm leading-relaxed text-muted-foreground">
          In short: the stage also genuinely no-ops in normal operation whenever sensitivity is
          Disabled, when there&rsquo;s no market-intelligence data for a date, or when no compset
          median can be computed. Today, that last condition is simply true for everyone, which is
          why you won&rsquo;t see Market Position adjustments in History just yet.
        </p>
      </GuideSection>

      <GuideSection title="Tips">
        <div className="space-y-3">
          <GuideTip type="do">
            Set your preference to <strong>Balanced</strong> now so the moment your compset starts
            collecting, Market Position is ready on a sensible default. You can always re-tune once
            you see real adjustments in History.
          </GuideTip>
          <GuideTip type="do">
            Keep a sane <strong>max-adjustment cap</strong>. It is your hard ceiling on how far
            market position alone can move a rate &mdash; a safety net that sits above the
            sensitivity preset&rsquo;s own cap.
          </GuideTip>
          <GuideTip type="dont">
            Don&rsquo;t expect any rate movement before the competitor-rate feed is live for your
            compset. Picking a sensitivity today configures the stage; it does not activate it.
          </GuideTip>
          <GuideTip type="dont">
            Don&rsquo;t treat Market Position as a replacement for Price Guardrails. It nudges you
            toward the market <em>within</em> your bounds &mdash; your floor, ceiling, and the
            guardrails are still the hard limits everything lives inside.
          </GuideTip>
        </div>
      </GuideSection>

      <GuideRelatedLinks
        links={[
          { slug: 'market-intelligence', title: 'Market Intelligence' },
          { slug: 'demand-response', title: 'Demand Response' },
          { slug: 'price-guardrails', title: 'Price Guardrails' },
          { slug: 'how-it-works', title: 'How It Works' },
        ]}
      />
    </GuideLayout>
  )
}
